import { ThemeDefaultParams, iconOverrides, iconSetAlpine, themeQuartz } from 'ag-grid-enterprise';

// specific material icons we want to use/override
const joinMaterialIcons = iconOverrides({
  // if any icons are missing from the generated Material Icons font,
  // see komodo-ui/public/fonts/material-ui-compression to add them
  cssImports: ['fonts/fonts-005.css'],
  type: 'font',
  family: 'Material',
  icons: {
    filter: 'filter_alt',
    'menu-alt': 'arrow_drop_down',
    'small-right': 'keyboard_arrow_right',
    cancel: 'close',
    grip: 'drag_indicator',
  },
});

const getJoinGridTheme = (themeOverrides: Partial<ThemeDefaultParams> = {}) =>
  themeQuartz
    .withParams({
      // Size + Font
      rowHeight: 32,
      iconSize: 16, // matches our icon-sm size
      fontSize: 14,
      fontFamily: ['Larsseit', 'sans-serif'],

      // Borders
      borderRadius: 0,
      rowBorder: false,
      menuBorder: false,
      wrapperBorder: true,
      inputBorder: false,
      inputFocusBorder: false,
      inputFocusShadow: false,
      cellEditingShadow: false,

      // Main Colors
      textColor: 'var(--colors-type-primary)',
      foregroundColor: 'var(--colors-type-primary)',
      backgroundColor: 'var(--colors-background-primary)',
      borderColor: 'var(--colors-border-muted)',
      iconColor: 'inherit',
      headerBackgroundColor: 'var(--colors-background-primary)',
      menuBackgroundColor: 'var(--colors-background-primary)',
      panelBackgroundColor: 'var(--colors-background-primary)',
      tooltipBackgroundColor: 'var(--background-1)',

      // Hover + Focus
      rowHoverColor: 'var(--colors-grid-hover)',
      headerCellHoverBackgroundColor: 'var(--colors-grid-hover)',
      // Per the docs, `accentColor` is the "color used for highlights and selection."
      // https://www.ag-grid.com/react-data-grid/theming-colors/#color-parameters
      // It's automatically lightened a bit for selection, so the full-saturation selection-focus-fill works best here
      accentColor: 'var(--colors-selection-focus-fill)',
      iconButtonActiveColor: 'var(--colors-type-primary)',
      iconButtonActiveBackgroundColor: 'transparent',
      iconButtonActiveIndicatorColor: 'var(--colors-badge-notification)',
      focusShadow: false,

      // Header
      headerFontSize: 16,
      headerFontWeight: 700,
      headerFontFamily: ['Larsseit', 'sans-serif'],
      headerColumnResizeHandleWidth: 0,

      // Checkboxes
      checkboxCheckedBackgroundColor: 'var(--colors-checkbox-selected)',
      checkboxUncheckedBackgroundColor: 'var(--colors-background-primary)',
      checkboxUncheckedBorderColor: 'var(--colors-checkbox-unselected)',
      checkboxIndeterminateBackgroundColor: 'var(--colors-checkbox-selected)',
      checkboxBorderWidth: 2,
      checkboxBorderRadius: 2,

      // Buttons
      iconButtonHoverBackgroundColor: 'var(--colors-button-icon-hover)',
      columnDropCellBackgroundColor: 'var(--colors-background-2)',
      columnDropCellDragHandleColor: 'var(--colors-checkbox-unselected)',
      iconButtonBorderRadius: 999,
      columnDropCellBorder: false,

      // Sidebar
      sideBarBackgroundColor: 'var(--colors-background-1)',
      sideButtonBarBackgroundColor: 'var(--colors-background-1)',
      sideButtonSelectedBackgroundColor: 'var(--colors-background-2)',
      sideButtonHoverBackgroundColor: 'var(--colors-background-2)',

      // Input
      inputPaddingStart: 15,

      // Menu
      // TODO: Upgrade Tailwind to v4 to enable usage of the named .shadow class
      menuShadow: '2px 6px 4px 0px rgba(52, 75, 54, 0.25)',
      // menu popover styling in theme-overrides.css

      ...themeOverrides,
    })
    // we can add custom SVG icons here if needed:
    // https://www.ag-grid.com/react-data-grid/custom-icons/#overriding-icons-with-images
    .withPart(iconSetAlpine) // fallback to alpine icon theme
    .withPart(joinMaterialIcons);

export const defaultTheme = getJoinGridTheme();
// TODO: bordered theme should be removed, in favor of more explicit distinctions for border/size distinctions
export const borderedTheme = getJoinGridTheme({
  rowBorder: true,
  wrapperBorder: false,
  rowHeight: 56,
});

export const gridTheme = getJoinGridTheme({
  rowHeight: 40,
  headerHeight: 40,

  borderColor: 'var(--colors-border-muted)',
  headerRowBorder: true,
  headerColumnBorder: true,
  columnBorder: true,
  rowBorder: true,
  wrapperBorder: false,
  wrapperBorderRadius: 0,
});

export const getTableTheme = (height: 'dense' | 'default') =>
  getJoinGridTheme({
    rowHeight: height === 'dense' ? 40 : 56,
    headerHeight: height === 'dense' ? 40 : 56,

    borderColor: 'var(--colors-border)',
    headerRowBorder: true,
    rowBorder: true,
    wrapperBorder: true,
  });
